/**
 * Project report builders + PDF launcher.
 *
 * Web port of mobile's
 *   /Users/gudupuramesh/Documents/siteexpens/src/features/projects/reports/*
 *   /Users/gudupuramesh/Documents/siteexpens/src/features/projects/TaskReportModal.tsx (buildReport)
 *
 * One source of truth for the report data shape + HTML template, so
 * a Progress Report generated on web matches what mobile generates
 * exactly. PDF "share" on web = open the HTML in a new tab and call
 * window.print() so the customer can save-as-PDF or print directly.
 */

import type { TaskItem } from '@/lib/useProjectTasks';
import type { ProjectListItem } from '@/lib/useProjects';

// ───── Domain types (mirror mobile) ─────

const STATUS_LABEL = {
  completed: 'DONE',
  ongoing: 'ONGOING',
  not_started: 'PENDING',
} as const;

const STATUS_COLOR = {
  completed: { bg: '#E3F5EB', fg: '#0F9D58' },
  ongoing: { bg: '#FEF3C7', fg: '#D97706' },
  not_started: { bg: '#F1F5F9', fg: '#475569' },
} as const;

const CATEGORY_LABEL: Record<string, string> = {
  electrical: 'Electrical',
  plumbing: 'Plumbing',
  woodwork: 'Woodwork',
  design: 'Design',
  cleaning: 'Cleaning',
  floor_cleaning: 'Floor Cleaning',
  floor_matting: 'Floor Matting',
  painting: 'Painting',
  false_ceiling: 'False Ceiling',
  tiling: 'Tiling',
  general: 'General',
};

function categoryLabelFor(key: string | undefined): string {
  if (!key) return 'General';
  return CATEGORY_LABEL[key] ?? key.replace(/_/g, ' ');
}

function formatDate(d: Date): string {
  return d.toLocaleDateString('en-IN', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
}

function clampProgress(n: number | undefined): number {
  if (typeof n !== 'number' || Number.isNaN(n)) return 0;
  return Math.max(0, Math.min(100, Math.round(n)));
}

// ───── Report builder (mirrors mobile's `buildReport`) ─────

export type ReportTaskRow = {
  id: string;
  title: string;
  description: string;
  category: string;
  assignee: string;
  status: 'completed' | 'ongoing' | 'not_started';
  start: string;
  end: string;
  durationDays: number;
  durationLabel: string;
  progress: number;
};

export type ReportCategoryRow = {
  key: string;
  label: string;
  count: number;
  totalDays: number;
  avgProgress: number;
};

export type ProjectReport = {
  projectName: string;
  projectAddress: string;
  clientName: string;
  generatedOn: string;
  periodStart: string | null;
  periodEnd: string | null;
  totalDays: number;
  workDays: number;
  total: number;
  completed: number;
  ongoing: number;
  notStarted: number;
  weightedProgress: number;
  categories: ReportCategoryRow[];
  taskRows: ReportTaskRow[];
};

export function buildProjectReport(
  project: ProjectListItem | null,
  tasks: TaskItem[],
): ProjectReport {
  const sorted = [...tasks].sort((a, b) => {
    const aT = a.startDate?.toMillis?.() ?? Number.MAX_SAFE_INTEGER;
    const bT = b.startDate?.toMillis?.() ?? Number.MAX_SAFE_INTEGER;
    return aT - bT;
  });

  const taskRows: ReportTaskRow[] = sorted.map((t) => {
    const startMs = t.startDate?.toMillis?.() ?? null;
    const endMs = t.endDate?.toMillis?.() ?? null;
    const start = startMs ? new Date(startMs) : null;
    const end = endMs ? new Date(endMs) : null;
    const durationDays =
      start && end
        ? Math.max(1, Math.round((end.getTime() - start.getTime()) / 86_400_000) + 1)
        : 0;
    const status: ReportTaskRow['status'] =
      t.status === 'completed' || t.status === 'ongoing'
        ? t.status
        : 'not_started';
    return {
      id: t.id,
      title: t.title ?? 'Untitled',
      description: t.description ?? '',
      category: categoryLabelFor(t.category),
      assignee: t.assignedToName ?? '',
      status,
      start: start ? formatDate(start) : '—',
      end: end ? formatDate(end) : '—',
      durationDays,
      durationLabel:
        durationDays > 0 ? `${durationDays} day${durationDays === 1 ? '' : 's'}` : '—',
      progress: clampProgress(t.progress),
    };
  });

  // Period: min(startDate) → max(endDate); fall back to project's
  // own start/end if tasks don't have them set.
  const startMillis = sorted
    .map((t) => t.startDate?.toMillis?.())
    .filter((n): n is number => typeof n === 'number');
  const endMillis = sorted
    .map((t) => t.endDate?.toMillis?.())
    .filter((n): n is number => typeof n === 'number');

  const periodStartMs = startMillis.length
    ? Math.min(...startMillis)
    : project?.startDate?.toMillis?.() ?? null;
  const periodEndMs = endMillis.length
    ? Math.max(...endMillis)
    : project?.endDate?.toMillis?.() ?? null;

  const periodStart = periodStartMs ? formatDate(new Date(periodStartMs)) : null;
  const periodEnd = periodEndMs ? formatDate(new Date(periodEndMs)) : null;
  const totalDays =
    periodStartMs && periodEndMs
      ? Math.max(0, Math.round((periodEndMs - periodStartMs) / 86_400_000) + 1)
      : 0;

  const workDays = taskRows.reduce((sum, t) => sum + t.durationDays, 0);

  const completed = taskRows.filter((t) => t.status === 'completed').length;
  const ongoing = taskRows.filter((t) => t.status === 'ongoing').length;
  const notStarted = taskRows.filter((t) => t.status === 'not_started').length;

  // Duration-weighted progress (matches mobile).
  const totalWeight = taskRows.reduce((s, t) => s + (t.durationDays || 1), 0);
  const weightedProgress =
    totalWeight === 0
      ? 0
      : Math.round(
          taskRows.reduce(
            (s, t) => s + t.progress * (t.durationDays || 1),
            0,
          ) / totalWeight,
        );

  const catMap = new Map<
    string,
    { count: number; totalDays: number; sumProgress: number }
  >();
  for (const t of taskRows) {
    const prev =
      catMap.get(t.category) ?? { count: 0, totalDays: 0, sumProgress: 0 };
    prev.count += 1;
    prev.totalDays += t.durationDays;
    prev.sumProgress += t.progress;
    catMap.set(t.category, prev);
  }
  const categories: ReportCategoryRow[] = Array.from(catMap.entries())
    .map(([label, v]) => ({
      key: label,
      label,
      count: v.count,
      totalDays: v.totalDays,
      avgProgress: Math.round(v.sumProgress / v.count),
    }))
    .sort((a, b) => b.totalDays - a.totalDays || b.count - a.count);

  return {
    projectName: project?.name ?? 'Untitled project',
    projectAddress:
      project?.siteAddress ?? project?.location ?? '',
    clientName: project?.client ?? '',
    generatedOn: formatDate(new Date()),
    periodStart,
    periodEnd,
    totalDays,
    workDays,
    total: taskRows.length,
    completed,
    ongoing,
    notStarted,
    weightedProgress,
    categories,
    taskRows,
  };
}

// ───── Progress Report HTML (verbatim from mobile) ─────

export function buildProgressReportHtml(d: ProjectReport): string {
  const cssBase = baseCss();

  const categoryRows = d.categories
    .map(
      (c) => `
        <tr>
          <td>${escape(c.label)}</td>
          <td class="num">${c.count}</td>
          <td class="num">${c.totalDays}</td>
          <td class="num">${c.avgProgress}%</td>
        </tr>`,
    )
    .join('');

  const taskRows = d.taskRows
    .map((t, i) => {
      const sc = STATUS_COLOR[t.status];
      return `
        <tr>
          <td class="idx">${String(i + 1).padStart(2, '0')}</td>
          <td>
            <div class="t-title">${escape(t.title)}</div>
            <div class="t-meta">${escape(t.category)}${
              t.assignee ? ` &middot; ${escape(t.assignee)}` : ''
            }</div>
            ${t.description ? `<div class="t-note">${escape(t.description)}</div>` : ''}
          </td>
          <td class="t-date">${escape(t.start)}</td>
          <td class="t-date">${escape(t.end)}</td>
          <td class="t-date">${escape(t.durationLabel)}</td>
          <td class="t-prog">
            <div class="pbar"><div class="pbar-fill" style="width:${t.progress}%"></div></div>
            <div class="pbar-label">${t.progress}%</div>
          </td>
          <td>
            <span class="pill" style="background:${sc.bg};color:${sc.fg}">
              ${STATUS_LABEL[t.status]}
            </span>
          </td>
        </tr>`;
    })
    .join('');

  return `<!DOCTYPE html>
<html lang="en">
<head>
<meta charset="utf-8" />
<title>${escape(d.projectName)} — Progress Report</title>
<style>${cssBase}</style>
</head>
<body>
  <header class="cover">
    <div class="brand">INTERIOR OS &middot; PROGRESS REPORT</div>
    <h1>${escape(d.projectName)}</h1>
    ${d.projectAddress ? `<div class="muted">${escape(d.projectAddress)}</div>` : ''}
    <div class="muted">Generated ${escape(d.generatedOn)}</div>
  </header>

  <section>
    <h2>Project period</h2>
    <div class="period">
      <div class="period-col">
        <div class="kicker">START</div>
        <div class="period-val">${escape(d.periodStart ?? '—')}</div>
      </div>
      <div class="period-arrow">&rarr;</div>
      <div class="period-col">
        <div class="kicker">END</div>
        <div class="period-val">${escape(d.periodEnd ?? '—')}</div>
      </div>
      <div class="period-col">
        <div class="kicker">TOTAL SPAN</div>
        <div class="period-val">${d.totalDays} days</div>
      </div>
      ${
        d.workDays > 0
          ? `<div class="period-col">
        <div class="kicker">WORK-DAYS</div>
        <div class="period-val">${d.workDays}</div>
      </div>`
          : ''
      }
    </div>
  </section>

  <section>
    <h2>Summary</h2>
    <div class="stats">
      <div class="stat">
        <div class="stat-val">${d.total}</div>
        <div class="stat-label">TOTAL TASKS</div>
      </div>
      <div class="stat stat-success">
        <div class="stat-val">${d.completed}</div>
        <div class="stat-label">COMPLETED</div>
      </div>
      <div class="stat stat-warning">
        <div class="stat-val">${d.ongoing}</div>
        <div class="stat-label">ONGOING</div>
      </div>
      <div class="stat stat-muted">
        <div class="stat-val">${d.notStarted}</div>
        <div class="stat-label">NOT STARTED</div>
      </div>
    </div>
    <div class="overall">
      <div class="overall-row">
        <div class="kicker">OVERALL PROGRESS (DURATION-WEIGHTED)</div>
        <div class="overall-val">${d.weightedProgress}%</div>
      </div>
      <div class="bar"><div class="bar-fill" style="width:${d.weightedProgress}%"></div></div>
    </div>
  </section>

  ${
    d.categories.length > 0
      ? `<section>
    <h2>By category</h2>
    <table class="cat">
      <thead>
        <tr>
          <th>CATEGORY</th>
          <th class="num">TASKS</th>
          <th class="num">DAYS</th>
          <th class="num">AVG %</th>
        </tr>
      </thead>
      <tbody>${categoryRows}</tbody>
    </table>
  </section>`
      : ''
  }

  <section>
    <h2>Detailed log &middot; ${d.taskRows.length} task${d.taskRows.length === 1 ? '' : 's'}</h2>
    ${
      d.taskRows.length === 0
        ? `<div class="empty">No tasks on this timeline yet.</div>`
        : `<table class="tasks">
      <thead>
        <tr>
          <th class="idx">#</th>
          <th>TASK</th>
          <th>START</th>
          <th>END</th>
          <th>DURATION</th>
          <th>PROGRESS</th>
          <th>STATUS</th>
        </tr>
      </thead>
      <tbody>${taskRows}</tbody>
    </table>`
    }
  </section>

  <footer>
    End of report &middot; Generated by InteriorIO
  </footer>
</body>
</html>`;
}

// ───── Timeline Agreement HTML (simpler client-facing summary) ─────

export function buildTimelineAgreementHtml(d: ProjectReport): string {
  const cssBase = baseCss();
  const taskRows = d.taskRows
    .map(
      (t, i) => `
        <tr>
          <td class="idx">${String(i + 1).padStart(2, '0')}</td>
          <td>
            <div class="t-title">${escape(t.title)}</div>
            <div class="t-meta">${escape(t.category)}</div>
          </td>
          <td class="t-date">${escape(t.start)}</td>
          <td class="t-date">${escape(t.end)}</td>
          <td class="t-date">${escape(t.durationLabel)}</td>
        </tr>`,
    )
    .join('');

  return `<!DOCTYPE html>
<html lang="en">
<head>
<meta charset="utf-8" />
<title>${escape(d.projectName)} — Timeline Agreement</title>
<style>${cssBase}</style>
</head>
<body>
  <header class="cover">
    <div class="brand">INTERIOR OS &middot; TIMELINE AGREEMENT</div>
    <h1>${escape(d.projectName)}</h1>
    ${d.clientName ? `<div class="muted">For: <b>${escape(d.clientName)}</b></div>` : ''}
    ${d.projectAddress ? `<div class="muted">${escape(d.projectAddress)}</div>` : ''}
    <div class="muted">Issued ${escape(d.generatedOn)}</div>
  </header>

  <section>
    <h2>Scope of work</h2>
    <p style="font-size:10.5pt; line-height:1.6; color:#0F172A;">
      This agreement lays out the planned timeline for the project
      above. ${d.total} task${d.total === 1 ? '' : 's'} have been
      scheduled across ${d.totalDays} day${d.totalDays === 1 ? '' : 's'}
      (${escape(d.periodStart ?? '—')} → ${escape(d.periodEnd ?? '—')}).
      Each task lists its planned start, end, and working duration.
      Both parties acknowledge that on-site conditions, material
      delivery delays, and client approvals may shift these dates.
    </p>
  </section>

  <section>
    <h2>Planned schedule</h2>
    ${
      d.taskRows.length === 0
        ? `<div class="empty">No tasks scheduled yet.</div>`
        : `<table class="tasks">
      <thead>
        <tr>
          <th class="idx">#</th>
          <th>TASK</th>
          <th>START</th>
          <th>END</th>
          <th>DURATION</th>
        </tr>
      </thead>
      <tbody>${taskRows}</tbody>
    </table>`
    }
  </section>

  <section>
    <h2>Signatures</h2>
    <div class="period" style="gap:24pt;">
      <div class="period-col">
        <div class="kicker">CLIENT</div>
        <div style="height:48pt; border-bottom:1px solid #94A3B8;"></div>
        <div class="muted" style="margin-top:6pt;">${escape(d.clientName || 'Client signature')}</div>
      </div>
      <div class="period-col">
        <div class="kicker">DESIGNER</div>
        <div style="height:48pt; border-bottom:1px solid #94A3B8;"></div>
        <div class="muted" style="margin-top:6pt;">Authorised signatory</div>
      </div>
    </div>
  </section>

  <footer>
    InteriorIO &middot; Issued ${escape(d.generatedOn)}
  </footer>
</body>
</html>`;
}

// ───── Site Report — comprehensive day-snapshot PDF ─────

/**
 * Site report = printable "today's situation" for a project on a
 * specific date. Joins data across DPR + attendance + material
 * requests + work logs + site photos + files repository, so a
 * studio owner can hand a single PDF to a client / GC and they
 * see everything that happened.
 *
 * Sections:
 *   1. Header — project name + address + report date
 *   2. Day status — work done, weather, on-track / blocked pill
 *   3. KPI strip — staff present / requests / value / files count
 *   4. Attendance — per-worker table
 *   5. Material requests today — table of requests with status
 *   6. Work logs — task progress posts from the day
 *   7. Site photos — thumbnails grid
 *   8. Tomorrow's plan — bullet list
 *   9. Issues / blockers — red callout (only when present)
 *  10. Files repository overview — total count + per-category
 */
export type SiteReportInputDpr = {
  workDone?: string;
  issues?: string;
  tomorrowPlan?: string;
  weather?: string;
  weatherNote?: string;
  photoUris?: string[];
};

export type SiteReportInputAttendance = {
  labourName: string;
  status: string;
  hours?: number | null;
};

export type SiteReportInputMaterialRequest = {
  id: string;
  title?: string;
  itemCount: number;
  status: string;
  totalValue: number;
  requestedByName?: string;
};

export type SiteReportInputWorkLog = {
  taskTitle: string;
  authorName: string;
  progress: number;
  deltaPct: number;
  text?: string;
  /** Photo URLs attached to this work log. Rendered inline in the
   *  Work Logs row AND aggregated into the Site Photos grid so the
   *  studio gets the full day's visual record in one place. */
  photoUris: string[];
};

export type SiteReportInputFiles = {
  /** Total file count across all categories. */
  total: number;
  /** Per-category counts — keys are the FILE_CATEGORIES keys. */
  byCategory: Record<string, number>;
};

export type SiteReportData = {
  projectName: string;
  projectAddress: string;
  clientName: string;
  reportDate: string;       // formatted, e.g. "22 May 2026"
  weekdayLabel: string;     // e.g. "Wednesday"
  generatedOn: string;
  // KPIs
  staffPresent: number;
  staffTotal: number;
  payableUnits: number;     // present + halfDay*0.5
  requestsCount: number;
  requestsValue: number;
  workLogCount: number;
  filesTotal: number;
  // DPR
  workDone: string;
  issues: string;
  tomorrowPlanItems: string[];
  weatherLabel: string;
  weatherNote: string;
  // Tables
  attendance: SiteReportInputAttendance[];
  materialRequests: SiteReportInputMaterialRequest[];
  workLogs: SiteReportInputWorkLog[];
  photoUris: string[];
  files: SiteReportInputFiles;
};

export type BuildSiteReportArgs = {
  project: ProjectListItem | null;
  reportDate: Date;
  weekdayLabel: string;
  dpr: SiteReportInputDpr | null;
  attendance: SiteReportInputAttendance[];
  attendanceSummary: { present: number; halfDay: number; total: number };
  materialRequests: SiteReportInputMaterialRequest[];
  workLogs: SiteReportInputWorkLog[];
  files: SiteReportInputFiles;
};

export function buildSiteReport(args: BuildSiteReportArgs): SiteReportData {
  const {
    project,
    reportDate,
    weekdayLabel,
    dpr,
    attendance,
    attendanceSummary,
    materialRequests,
    workLogs,
    files,
  } = args;

  const tomorrowPlanItems = (dpr?.tomorrowPlan ?? '')
    .split(/\r?\n/)
    .map((s) => s.trim())
    .filter(Boolean);

  const payableUnits =
    attendanceSummary.present + attendanceSummary.halfDay * 0.5;

  const requestsValue = materialRequests.reduce(
    (s, r) => s + (r.totalValue ?? 0),
    0,
  );

  // Aggregate every photo the day produced — DPR-direct photos plus
  // every work-log attachment. De-dupe by URL so a photo attached to
  // both a work log and the DPR doesn't render twice. Order: DPR
  // photos first (taken by the supervisor as overview shots),
  // followed by work-log photos in the order logs were posted.
  const seen = new Set<string>();
  const allPhotos: string[] = [];
  for (const u of dpr?.photoUris ?? []) {
    if (u && !seen.has(u)) {
      seen.add(u);
      allPhotos.push(u);
    }
  }
  for (const log of workLogs) {
    for (const u of log.photoUris) {
      if (u && !seen.has(u)) {
        seen.add(u);
        allPhotos.push(u);
      }
    }
  }

  return {
    projectName: project?.name ?? 'Untitled project',
    projectAddress: project?.siteAddress ?? project?.location ?? '',
    clientName: project?.client ?? '',
    reportDate: formatDate(reportDate),
    weekdayLabel,
    generatedOn: formatDate(new Date()),
    staffPresent: attendanceSummary.present,
    staffTotal: attendanceSummary.total,
    payableUnits,
    requestsCount: materialRequests.length,
    requestsValue,
    workLogCount: workLogs.length,
    filesTotal: files.total,
    workDone: (dpr?.workDone ?? '').trim(),
    issues: (dpr?.issues ?? '').trim(),
    tomorrowPlanItems,
    weatherLabel: dpr?.weather ?? '',
    weatherNote: (dpr?.weatherNote ?? '').trim(),
    attendance,
    materialRequests,
    workLogs,
    photoUris: allPhotos,
    files,
  };
}

const ATT_STATUS_LABEL: Record<string, string> = {
  present: 'Present',
  half_day: 'Half day',
  absent: 'Absent',
  paid_leave: 'Paid leave',
  week_off: 'Week off',
};

const ATT_STATUS_COLOR: Record<string, { bg: string; fg: string }> = {
  present:    { bg: '#E3F5EB', fg: '#0F9D58' },
  half_day:   { bg: '#FEF3C7', fg: '#B45309' },
  absent:     { bg: '#FEE2E2', fg: '#B91C1C' },
  paid_leave: { bg: '#EDE9FE', fg: '#6D28D9' },
  week_off:   { bg: '#F1F5F9', fg: '#475569' },
};

const MAT_STATUS_LABEL: Record<string, string> = {
  pending: 'Pending',
  approved: 'Approved',
  rejected: 'Rejected',
};

const MAT_STATUS_COLOR: Record<string, { bg: string; fg: string }> = {
  pending:  { bg: '#FEF3C7', fg: '#B45309' },
  approved: { bg: '#E3F5EB', fg: '#0F9D58' },
  rejected: { bg: '#FEE2E2', fg: '#B91C1C' },
};

const FILE_CATEGORY_LABEL: Record<string, string> = {
  design_2d: '2D',
  design_3d: '3D',
  layout:    'Layout',
  mom:       'MOM',
  agreement: 'Agreement',
  other:     'Other',
};

function fmtInr(n: number): string {
  return '₹' + Math.round(n).toLocaleString('en-IN');
}

export function buildSiteReportHtml(d: SiteReportData): string {
  const cssBase = baseCss();

  const attendanceRows = d.attendance.length
    ? d.attendance
        .map((r) => {
          const c = ATT_STATUS_COLOR[r.status] ?? {
            bg: '#F1F5F9',
            fg: '#475569',
          };
          const label = ATT_STATUS_LABEL[r.status] ?? r.status;
          return `
          <tr>
            <td>${escape(r.labourName)}</td>
            <td>
              <span class="pill" style="background:${c.bg};color:${c.fg}">
                ${escape(label.toUpperCase())}
              </span>
            </td>
            <td class="num">${
              typeof r.hours === 'number' ? r.hours.toFixed(1) + ' h' : '—'
            }</td>
          </tr>`;
        })
        .join('')
    : `<tr><td colspan="3" class="empty">No attendance marked for this day.</td></tr>`;

  const materialRows = d.materialRequests.length
    ? d.materialRequests
        .map((r) => {
          const c = MAT_STATUS_COLOR[r.status] ?? {
            bg: '#F1F5F9',
            fg: '#475569',
          };
          const label = MAT_STATUS_LABEL[r.status] ?? r.status;
          return `
          <tr>
            <td>
              <div class="t-title">${escape(r.title ?? `Request ${r.id.slice(-6)}`)}</div>
              ${
                r.requestedByName
                  ? `<div class="t-meta">${escape(r.requestedByName)}</div>`
                  : ''
              }
            </td>
            <td class="num">${r.itemCount} item${r.itemCount === 1 ? '' : 's'}</td>
            <td class="num">${fmtInr(r.totalValue)}</td>
            <td>
              <span class="pill" style="background:${c.bg};color:${c.fg}">
                ${escape(label.toUpperCase())}
              </span>
            </td>
          </tr>`;
        })
        .join('')
    : `<tr><td colspan="4" class="empty">No material requests on this day.</td></tr>`;

  const workLogRows = d.workLogs.length
    ? d.workLogs
        .map((u) => {
          const deltaStr =
            u.deltaPct > 0
              ? `+${u.deltaPct}%`
              : u.deltaPct < 0
                ? `${u.deltaPct}%`
                : `${u.progress}%`;
          const deltaColor =
            u.deltaPct > 0
              ? '#0F9D58'
              : u.deltaPct < 0
                ? '#B91C1C'
                : '#475569';
          // Inline thumbnails — up to 4 per row to keep the row
          // height controlled. A "+N more" badge surfaces overflow.
          const thumbs = u.photoUris.slice(0, 4)
            .map(
              (url) =>
                `<img class="wl-thumb" src="${escape(url)}" alt="Work log photo" />`,
            )
            .join('');
          const extra = u.photoUris.length > 4
            ? `<span class="wl-thumb-more">+${u.photoUris.length - 4}</span>`
            : '';
          const photosCell = u.photoUris.length
            ? `<div class="wl-thumb-row">${thumbs}${extra}</div>`
            : '<span class="wl-no-photos">—</span>';
          return `
          <tr>
            <td>
              <div class="wl-title">${escape(u.taskTitle)}</div>
              <div class="wl-author">by ${escape(u.authorName)}</div>
              ${u.text ? `<div class="wl-note">${escape(u.text)}</div>` : ''}
            </td>
            <td class="num" style="color:${deltaColor};font-weight:700">${deltaStr}</td>
            <td>${photosCell}</td>
          </tr>`;
        })
        .join('')
    : `<tr><td colspan="3" class="empty">No work-log entries posted on this day.</td></tr>`;

  const photosHtml = d.photoUris.length
    ? `<div class="photo-grid">
         ${d.photoUris
           .map(
             (u) =>
               `<img class="photo" src="${escape(u)}" alt="Site photo" />`,
           )
           .join('')}
       </div>`
    : `<div class="empty-card">No site photos uploaded for this day.</div>`;

  const fileCategoryChips = Object.entries(d.files.byCategory)
    .filter(([, n]) => n > 0)
    .map(
      ([key, n]) =>
        `<span class="fpill">${escape(FILE_CATEGORY_LABEL[key] ?? key)} · ${n}</span>`,
    )
    .join('');

  const issuesHtml = d.issues
    ? `<section>
         <h2>Site issues / blockers</h2>
         <div class="issues">${escape(d.issues)}</div>
       </section>`
    : '';

  const tomorrowHtml = d.tomorrowPlanItems.length
    ? `<section>
         <h2>Tomorrow's plan</h2>
         <ul class="plan">
           ${d.tomorrowPlanItems
             .map((t) => `<li>${escape(t)}</li>`)
             .join('')}
         </ul>
       </section>`
    : '';

  return `<!DOCTYPE html>
<html lang="en">
<head>
<meta charset="utf-8" />
<title>${escape(d.projectName)} — Site Report ${escape(d.reportDate)}</title>
<style>
${cssBase}
/* ── Site report extras ── */

/* Page-flow overrides. The baseCss sets sections to
 * page-break-inside: avoid which prevents content splitting — fine
 * for the Progress Reports small sections but it leaves huge gaps
 * on the Site Report when a long table (Work Logs with 8+ rows,
 * large photo grid) does not fit in the remaining page space.
 * Override:
 *   - Sections CAN split; rows within tables CANNOT.
 *   - Cover stays together (small, looks bad when split).
 *   - Photo grids flow naturally — individual photos can wrap to
 *     the next page but will not be cut in half mid-image.
 *   - Headings (h2) stick to the next lines so we do not get
 *     orphan headings at the bottom of a page. */
section { page-break-inside: auto; }
.cover { page-break-inside: avoid; page-break-after: auto; }
h2 { page-break-after: avoid; }
table { page-break-inside: auto; }
thead { display: table-header-group; }   /* repeat headers on every page */
tfoot { display: table-footer-group; }
tr   { page-break-inside: avoid; page-break-after: auto; }
img  { page-break-inside: avoid; }
/* Lists + small callouts — keep together */
.plan, .issues, .day-status, .stats, .files-overview, .empty-card {
  page-break-inside: avoid;
}

.day-status {
  padding: 10pt 12pt;
  border: 1px solid #E2E8F0;
  margin-bottom: 12pt;
  display: flex; flex-wrap: wrap;
  align-items: center; gap: 8pt;
}
.day-status .label {
  font-family: Menlo, Consolas, monospace;
  font-size: 8pt; font-weight: 700; color: #94A3B8;
  letter-spacing: 1.4px;
}
.day-status .pill {
  font-size: 9pt; padding: 4pt 8pt; border-radius: 999pt;
  font-weight: 700;
}
.day-status-body {
  flex-basis: 100%;
  margin-top: 8pt;
  padding-top: 8pt;
  border-top: 1px solid #EEF2F7;
  font-size: 10.5pt;
  line-height: 1.5;
  white-space: pre-wrap;
}
.day-status-body.muted-body { color: #94A3B8; font-style: italic; }
.empty {
  text-align: center; color: #94A3B8; font-style: italic;
  padding: 12pt 4pt !important;
}
.empty-card {
  border: 1px dashed #E2E8F0; padding: 14pt; text-align: center;
  color: #94A3B8; font-size: 10pt;
}
.issues {
  padding: 10pt 12pt;
  background: #FEF2F2;
  border: 1px solid #FECACA;
  color: #991B1B;
  font-size: 10.5pt;
  white-space: pre-wrap;
  line-height: 1.5;
}
.plan { margin: 0; padding-left: 18pt; }
.plan li { font-size: 10.5pt; margin-bottom: 3pt; }
/* The earlier .work-done rule was replaced by .day-status-body
 * (combined into the same bordered shell as the status pill). */
.photo-grid {
  display: flex; flex-wrap: wrap; gap: 6pt;
}
.photo {
  width: 90pt; height: 90pt; object-fit: cover;
  border: 1px solid #E2E8F0;
}
.files-overview {
  display: flex; align-items: center; gap: 8pt;
  flex-wrap: wrap; padding: 10pt 12pt;
  border: 1px solid #E2E8F0;
}
.files-total {
  font-size: 22pt; font-weight: 800; letter-spacing: -0.4px;
  margin-right: 8pt;
}
.fpill {
  display: inline-flex;
  padding: 3pt 8pt; border-radius: 999pt;
  background: #F1F5F9; color: #475569;
  font-size: 9pt; font-weight: 700;
  font-family: Menlo, Consolas, monospace;
  letter-spacing: 0.8px;
}
.t-title { font-weight: 700; font-size: 10.5pt; line-height: 1.3; }
.t-meta {
  font-family: Menlo, Consolas, monospace;
  font-size: 7.5pt; font-weight: 600;
  color: #94A3B8; letter-spacing: 0.8px;
  margin-top: 2pt;
}
.t-note {
  font-size: 9pt; color: #475569; margin-top: 3pt;
  white-space: pre-wrap; line-height: 1.4;
}
.pill {
  display: inline-block;
  padding: 2pt 7pt; border-radius: 999pt;
  font-size: 8pt; font-weight: 700;
  letter-spacing: 0.6px;
}

/* Work-logs table — own classes to avoid baseCss collision
   (the original .tasks .t-meta paints the author name red /
   uppercase mono, which made names read like an error tag). */
.wl-table th { background: #F8FAFC; }
.wl-table td { vertical-align: top; padding: 8pt 8pt; }
.wl-title {
  font-weight: 700;
  font-size: 10.5pt;
  color: #15171A;
  line-height: 1.3;
}
.wl-author {
  font-size: 8.5pt;
  color: #94A3B8;
  margin-top: 2pt;
  font-weight: 500;
  letter-spacing: 0;
  text-transform: none;
}
.wl-note {
  font-size: 9pt;
  color: #475569;
  margin-top: 4pt;
  white-space: pre-wrap;
  line-height: 1.45;
}
.wl-thumb-row {
  display: flex;
  flex-wrap: wrap;
  gap: 4pt;
  align-items: center;
}
.wl-thumb {
  width: 40pt;
  height: 40pt;
  object-fit: cover;
  border: 1px solid #E2E8F0;
  border-radius: 3pt;
}
.wl-thumb-more {
  width: 40pt; height: 40pt;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: 1px solid #E2E8F0;
  border-radius: 3pt;
  background: #F1F5F9;
  color: #475569;
  font-size: 9pt;
  font-weight: 700;
}
.wl-no-photos {
  color: #CBD5E1;
  font-size: 12pt;
}
</style>
</head>
<body>
  <header class="cover">
    <div class="brand">INTERIOR OS &middot; SITE REPORT</div>
    <h1>${escape(d.projectName)}</h1>
    ${d.projectAddress ? `<div class="muted">${escape(d.projectAddress)}</div>` : ''}
    ${d.clientName ? `<div class="muted">Client: ${escape(d.clientName)}</div>` : ''}
    <div class="muted">${escape(d.reportDate)} &middot; ${escape(d.weekdayLabel)}</div>
    <div class="muted">Generated ${escape(d.generatedOn)}</div>
  </header>

  <section>
    <h2>Day status</h2>
    <div class="day-status">
      ${
        d.issues
          ? `<span class="pill" style="background:#FEE2E2;color:#B91C1C">BLOCKED</span>`
          : d.workDone
            ? `<span class="pill" style="background:#E3F5EB;color:#0F9D58">ON TRACK</span>`
            : `<span class="pill" style="background:#F1F5F9;color:#475569">NO LOG</span>`
      }
      ${
        d.weatherLabel
          ? `<span class="label">WEATHER · ${escape(d.weatherLabel.toUpperCase())}${d.weatherNote ? ' · ' + escape(d.weatherNote) : ''}</span>`
          : ''
      }
      ${
        d.workDone
          ? `<div class="day-status-body">${escape(d.workDone)}</div>`
          : `<div class="day-status-body muted-body">No work summary recorded for this day.</div>`
      }
    </div>
  </section>

  <section>
    <h2>Day snapshot</h2>
    <div class="stats">
      <div class="stat">
        <div class="stat-val">${d.staffPresent}<span style="font-size:13pt;color:#94A3B8">/${d.staffTotal}</span></div>
        <div class="stat-label">STAFF PRESENT</div>
      </div>
      <div class="stat">
        <div class="stat-val">${d.payableUnits.toFixed(1)}</div>
        <div class="stat-label">PAYABLE UNITS</div>
      </div>
      <div class="stat">
        <div class="stat-val">${d.requestsCount}</div>
        <div class="stat-label">REQUESTS</div>
      </div>
      <div class="stat">
        <div class="stat-val">${fmtInr(d.requestsValue)}</div>
        <div class="stat-label">VALUE</div>
      </div>
      <div class="stat">
        <div class="stat-val">${d.workLogCount}</div>
        <div class="stat-label">WORK LOGS</div>
      </div>
      <div class="stat">
        <div class="stat-val">${d.filesTotal}</div>
        <div class="stat-label">FILES TOTAL</div>
      </div>
    </div>
  </section>

  <section>
    <h2>Attendance — ${d.attendance.length} record${d.attendance.length === 1 ? '' : 's'}</h2>
    <table>
      <thead><tr><th>Worker</th><th>Status</th><th class="num">Hours</th></tr></thead>
      <tbody>${attendanceRows}</tbody>
    </table>
  </section>

  <section>
    <h2>Material requests — ${d.materialRequests.length} on this day</h2>
    <table>
      <thead>
        <tr>
          <th>Request</th>
          <th class="num">Items</th>
          <th class="num">Value</th>
          <th>Status</th>
        </tr>
      </thead>
      <tbody>${materialRows}</tbody>
    </table>
  </section>

  <section>
    <h2>Work logs — ${d.workLogs.length} update${d.workLogs.length === 1 ? '' : 's'}</h2>
    <table class="wl-table">
      <thead>
        <tr>
          <th style="width:55%">Task &amp; note</th>
          <th class="num" style="width:14%">Δ Progress</th>
          <th style="width:31%">Photos</th>
        </tr>
      </thead>
      <tbody>${workLogRows}</tbody>
    </table>
  </section>

  <section>
    <h2>Site photos — ${d.photoUris.length}</h2>
    ${photosHtml}
  </section>

  ${tomorrowHtml}

  ${issuesHtml}

  <section>
    <h2>Project files</h2>
    ${
      d.files.total > 0
        ? `<div class="files-overview">
             <div class="files-total">${d.files.total}</div>
             <div style="flex:1;display:flex;flex-wrap:wrap;gap:4pt">
               ${fileCategoryChips}
             </div>
           </div>`
        : `<div class="empty-card">No files uploaded to this project yet.</div>`
    }
  </section>

  <div class="muted" style="text-align:center;margin-top:18pt">
    Generated by InteriorIO &middot; ${escape(d.generatedOn)}
  </div>
</body>
</html>`;
}

// ───── Browser print launcher ─────

/**
 * Open the HTML in a new tab and auto-trigger the browser's print
 * dialog so the user can save-as-PDF (or print directly). Mirrors
 * the mobile `generateAndShareWebPdf` UX without requiring native
 * print bindings.
 */
export function openHtmlForPrint(html: string, title: string): void {
  const win = window.open('', '_blank');
  if (!win) {
    alert(
      'Pop-up blocked. Allow pop-ups for this site to download the report.',
    );
    return;
  }
  win.document.title = title;
  win.document.open();
  win.document.write(html);
  win.document.close();
  // Defer print until the new tab has finished parsing + rendering
  // CSS — without this, Chrome sometimes opens the print dialog
  // against a blank page.
  win.addEventListener('load', () => {
    setTimeout(() => {
      try {
        win.focus();
        win.print();
      } catch {
        // No-op — user can still hit Cmd/Ctrl+P manually.
      }
    }, 250);
  });
}

// ───── Helpers ─────

function escape(s: string): string {
  return String(s ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

function baseCss(): string {
  return `
    @page { size: A4; margin: 0; }
    * { box-sizing: border-box; }
    html, body {
      margin: 0;
      font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Helvetica, Arial, sans-serif;
      color: #0F172A;
      font-size: 11pt;
      line-height: 1.4;
    }
    body { padding: 14mm 12mm; }

    .muted { color: #475569; font-size: 9pt; margin-top: 2pt; }
    .kicker {
      font-family: Menlo, Consolas, monospace;
      font-size: 7.5pt; font-weight: 700;
      color: #94A3B8; letter-spacing: 1.4px;
    }
    .num { text-align: right; font-variant-numeric: tabular-nums; }

    .cover {
      border-bottom: 1px solid #E2E8F0;
      padding-bottom: 14pt;
      margin-bottom: 18pt;
    }
    .brand {
      font-family: Menlo, Consolas, monospace;
      font-size: 8pt; font-weight: 700;
      color: #DC2626; letter-spacing: 1.6px;
    }
    h1 {
      font-size: 22pt; margin: 6pt 0 4pt 0;
      font-weight: 800; letter-spacing: -0.5px;
    }

    section { margin-bottom: 16pt; page-break-inside: avoid; }
    h2 {
      font-family: Menlo, Consolas, monospace;
      font-size: 8pt; font-weight: 700;
      color: #94A3B8; letter-spacing: 1.4px;
      text-transform: uppercase;
      margin: 0 0 6pt 0;
      padding-bottom: 4pt;
      border-bottom: 1px solid #EEF2F7;
    }

    .period {
      display: flex; align-items: center; gap: 12pt;
      padding: 8pt 10pt;
      border: 1px solid #E2E8F0;
    }
    .period-col { flex: 1; }
    .period-val {
      font-size: 13pt; font-weight: 700; margin-top: 2pt;
      letter-spacing: -0.2px;
    }
    .period-arrow { color: #94A3B8; font-size: 14pt; }

    .stats { display: flex; gap: 8pt; margin-bottom: 10pt; }
    .stat {
      flex: 1; padding: 10pt;
      background: #F8FAFC;
      border: 1px solid #EEF2F7;
    }
    .stat-success { background: #E3F5EB; border-color: #C7E8D5; }
    .stat-warning { background: #FEF3C7; border-color: #F0E2A2; }
    .stat-muted   { background: #F1F5F9; border-color: #E2E8F0; }
    .stat-val { font-size: 22pt; font-weight: 800; letter-spacing: -0.4px; }
    .stat-label {
      font-family: Menlo, Consolas, monospace;
      font-size: 7.5pt; font-weight: 700; letter-spacing: 1.2px;
      margin-top: 2pt;
    }

    .overall { padding: 8pt 10pt; border: 1px solid #E2E8F0; }
    .overall-row {
      display: flex; justify-content: space-between; align-items: center;
      margin-bottom: 4pt;
    }
    .overall-val { font-size: 12pt; font-weight: 700; color: #DC2626; }
    .bar { height: 7pt; background: #F1F5F9; border-radius: 3pt; overflow: hidden; }
    .bar-fill { height: 100%; background: #DC2626; }

    table { width: 100%; border-collapse: collapse; }
    th, td {
      padding: 6pt 8pt;
      text-align: left;
      border-bottom: 1px solid #EEF2F7;
      font-size: 10pt;
      vertical-align: top;
    }
    th {
      font-family: Menlo, Consolas, monospace;
      font-size: 7.5pt; font-weight: 700;
      color: #475569; letter-spacing: 1.2px;
      border-bottom: 1px solid #E2E8F0;
      background: #F8FAFC;
    }
    .cat tr:nth-child(even) { background: #FBFBFD; }

    .tasks .idx {
      font-family: Menlo, Consolas, monospace;
      font-size: 9pt; font-weight: 700; color: #94A3B8;
      width: 26pt;
    }
    .tasks .t-title { font-weight: 700; font-size: 11pt; line-height: 1.3; }
    .tasks .t-meta {
      font-family: Menlo, Consolas, monospace;
      font-size: 7.5pt; font-weight: 600;
      color: #DC2626; letter-spacing: 0.8px;
      margin-top: 2pt;
    }
    .tasks .t-note { font-size: 9pt; color: #475569; margin-top: 3pt; }
    .tasks .t-date {
      font-size: 9.5pt; font-weight: 600;
      white-space: nowrap; width: 60pt;
    }
    .tasks .t-prog { width: 90pt; }
    .pbar { height: 5pt; background: #F1F5F9; overflow: hidden; border-radius: 2.5pt; }
    .pbar-fill { height: 100%; background: #DC2626; }
    .pbar-label {
      font-family: Menlo, Consolas, monospace;
      font-size: 8pt; font-weight: 700; color: #475569;
      margin-top: 2pt;
    }
    .pill {
      display: inline-block;
      padding: 2pt 6pt;
      border-radius: 9pt;
      font-family: Menlo, Consolas, monospace;
      font-size: 7.5pt; font-weight: 700; letter-spacing: 0.8px;
      white-space: nowrap;
    }
    .empty { padding: 14pt; color: #475569; text-align: center; font-size: 10pt; }

    footer {
      margin-top: 24pt;
      padding-top: 8pt;
      text-align: center;
      font-family: Menlo, Consolas, monospace;
      font-size: 7.5pt;
      color: #94A3B8;
      letter-spacing: 1.2px;
      border-top: 1px solid #EEF2F7;
    }

    @media print {
      body { padding: 14mm 12mm; }
    }
  `;
}
